// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { advisoryApi } from '@shared/data-access/api/brokerage/advisory-api.v2';
import { useMemo } from 'react';
import type { AccountCurrency } from '@shared/data-access/types/brokerage-types';

export interface UseAccountsTransactionsProps {
  clientId?: number;
  ibans: string[];
  start_date: string;
  institutionId: string;
  skip?: boolean;
  currency: AccountCurrency;
}

export const useAccountsTransactions = ({
  clientId,
  ibans,
  start_date,
  institutionId,
  skip,
  currency,
}: UseAccountsTransactionsProps) => {
  const accountsTransactionsQuery =
    advisoryApi.useGetAdvisoryAccountsTransactionsQuery(
      {
        client_id: clientId,
        start_date,
        institution_id: institutionId,
        currency,
      },
      { skip: skip },
    );

  const accountsTransactions = useMemo(() => {
    const data = accountsTransactionsQuery.data ?? [];

    return data.filter(account => ibans.includes(account.iban));
  }, [accountsTransactionsQuery.data, ibans]);

  const transactions = useMemo(() => {
    return accountsTransactions.flatMap(account => account.transactions);
  }, [accountsTransactions]);

  const hasTransactions = useMemo(() => {
    return transactions.length > 0;
  }, [transactions.length]);

  return {
    accountsTransactions,
    hasTransactions,
    accountsTransactionsQuery,
    transactions,
  };
};
